import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  useNavigate, useParams, useSearchParams
} from 'react-router-dom';
import { batch, useDispatch, useSelector } from 'react-redux';
import { PAGE_SIZE, SCENARIO } from '../../../constants/general';
import { getIndustriesListAction, getTrendCategoryAction } from '../../../store/general/actions';
import {
  cleanAdminTrends,
  deleteScenarioTrendAction,
  getScenarioAction,
  getScenarioTrendsAction
} from '../../../store/admin/adminTrends/actions';
import AdminPage from '../../../layout/admin/AdminPage/AdminPage';
import Title from '../../../components/Title/Title';
import Button from '../../../components/Button/Button';
import AdminTrendFilter from './components/AdminTrendFilter';
import Pagination from '../../../components/Pagination/Pagination';
import ModalDelete from '../../../components/ModalDelete/ModalDelete';
import AdminTrendTrends from './components/AdminTrendTrends';
import styles from './sass/AdminTrends.module.scss';

import { ReactComponent as PlusIcon } from '../../../assets/icons/plusBlack.svg';
import { ReactComponent as ChevronIcon } from '../../../assets/icons/chevronLeft.svg';

const AdminScenarioTrends = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [idTrend, setIdTrend] = useState(null);

  const [filterParam, setFilterParam] = useState({
    name: searchParams.get('name') || '',
    category: +searchParams.get('category') || null,
    industry: +searchParams.get('industry') || null,
    end: searchParams.get('end') || null,
    start: searchParams.get('start') || null,
    page: +searchParams.get('page') || 1,
    sortOrder: searchParams.get('sortOrder') || 'DESC',
    sortBy: searchParams.get('sortBy') || 'created_at',
  });

  const {
    adminTrends: {
      loading, trends, totalCount, scenario
    },
    general: { categoryList, industriesList, fromPath }
  } = useSelector(({ adminTrends, general }) => ({ adminTrends, general }));

  useEffect(() => {
    batch(() => {
      dispatch(getTrendCategoryAction());
      dispatch(getIndustriesListAction());
      dispatch(getScenarioTrendsAction(id, filterParam));
      dispatch(getScenarioAction(id));
    });
    return () => {
      dispatch(cleanAdminTrends());
    };
  }, [id]);

  const handleSearchCategoryIndustry = useCallback((value, field) => {
    setFilterParam((prevState) => ({ ...prevState, [field]: value === prevState[field] ? null : value }));
  }, []);

  const handleSearchInput = useCallback((event) => {
    setFilterParam((prevState) => ({ ...prevState, [event.target.name]: event.target.value }));
  }, []);

  const handleSearchDate = useCallback((start, end) => {
    setFilterParam((prevState) => ({ ...prevState, start, end }));
  }, []);

  const handlePagination = useCallback((page) => {
    setFilterParam({ ...filterParam, page });
  }, [filterParam]);

  const handleFilter = useCallback(() => {
    setFilterParam({ ...filterParam, end: filterParam.end || filterParam.start, page: 1 });
    dispatch(getScenarioTrendsAction(
      id,
      { ...filterParam, end: filterParam.end || filterParam.start, page: 1 },
      setSearchParams
    ));
  }, [filterParam]);

  const handleDeleteTrend = useCallback(() => {
    if (trends.length === 1 && filterParam.page > 1) {
      setFilterParam({ ...filterParam, page: filterParam.page - 1 });
      dispatch(deleteScenarioTrendAction(id, idTrend, { ...filterParam, page: filterParam.page - 1 }, setSearchParams));
    } else {
      dispatch(deleteScenarioTrendAction(id, idTrend, filterParam));
    }
    setIdTrend(null);
  }, [filterParam, trends, idTrend, id]);

  const handleSort = useCallback((sortBy, sortOrder) => {
    setFilterParam({ ...filterParam, sortBy, sortOrder });
    dispatch(getScenarioTrendsAction(id, { ...filterParam, sortBy, sortOrder }, setSearchParams));
  }, [filterParam]);

  const handleBackNavigation = useCallback(() => {
    navigate(fromPath || '/admin/dashboard/scenarios');
  }, [fromPath]);

  return (
    <AdminPage pageContainer={styles.container} loader={loading} activeTab={SCENARIO}>
      <div className={styles.content}>
        <div className={styles.logo} />
        <Title text={t('administration')} style={styles.title} />
        <Button
          text={scenario?.name}
          icon={<ChevronIcon />}
          style={styles.scenarioBackButtonContainer}
          styleButton={styles.scenarioBackButton}
          onClick={handleBackNavigation}
        />
        <div className={styles.subtitle}>
          <Title text={t('trends')} Type="h3" style={styles.subtitleText} />
          <Button
            text={t('addScenarioTrend')}
            icon={<PlusIcon />}
            style={styles.buttonContainer}
            styleButton={styles.button}
            onClick={() => navigate('create')}
          />
        </div>
        <AdminTrendFilter
          trend={filterParam.name}
          category={filterParam.category}
          industry={filterParam.industry}
          start={filterParam.start}
          end={filterParam.end}
          categoryList={categoryList}
          industriesList={industriesList}
          onChange={handleSearchInput}
          onChangeCategoryIndustry={handleSearchCategoryIndustry}
          onChangeDate={handleSearchDate}
          onClick={handleFilter}
        />

        <AdminTrendTrends
          trends={trends}
          onDelete={setIdTrend}
          sortBy={filterParam.sortBy}
          sortOrder={filterParam.sortOrder}
          industriesList={industriesList}
          onSort={handleSort}
        />

        <Pagination
          currentPage={filterParam.page}
          totalCount={totalCount}
          pageSize={PAGE_SIZE}
          onPageChange={(page) => handlePagination(page)}
        />
      </div>

      {!!idTrend && (
        <ModalDelete
          onDelete={handleDeleteTrend}
          onClose={() => setIdTrend(null)}
          text={t('trend')}
        />
      )}
    </AdminPage>
  );
};

export default AdminScenarioTrends;
